import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ArrowRight, Printer, Plus, Wallet, Phone, Mail, Building2, User, FileText, ClipboardList, Handshake, Receipt } from 'lucide-react';
import { getCustomerCard, updateCustomer } from '../api';
import { toast } from '../hooks/use-toast';
import { methodLabel } from '../lib/printDocument';

const money = (v) => `₪${(parseFloat(v) || 0).toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('he-IL') : '-');
const SOURCE_LABELS = { daily_log: 'רישום יומי', deal: 'סגירת עסקה', consolidated: 'חשבון מרוכז' };

const StatBox = ({ label, value, tone, testId }) => (
  <div className={`rounded-xl border-2 p-4 ${tone}`} data-testid={testId}>
    <p className="text-xs font-semibold opacity-80">{label}</p>
    <p className="text-2xl font-black mt-1">{value}</p>
  </div>
);

const Section = ({ icon: Icon, title, count, children, testId }) => (
  <section className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden mb-6 break-inside-avoid" data-testid={testId}>
    <header className="flex items-center gap-2 px-5 py-3 bg-slate-800 text-white print:bg-gray-200 print:text-black">
      <Icon className="w-5 h-5" />
      <h2 className="font-bold">{title}</h2>
      <span className="text-xs bg-white/20 rounded-full px-2 py-0.5 print:bg-white">{count}</span>
    </header>
    {count === 0 ? <p className="p-5 text-sm text-gray-400">אין רשומות</p> : <div className="overflow-x-auto">{children}</div>}
  </section>
);

const Table = ({ head, rows }) => (
  <table className="w-full text-sm border-collapse">
    <thead><tr className="bg-gray-50 text-gray-600">{head.map((h) => <th key={h} className="p-3 text-right font-semibold border-b">{h}</th>)}</tr></thead>
    <tbody>{rows}</tbody>
  </table>
);

const td = 'p-3 border-b border-gray-100';

const LogsTable = ({ logs, onOpen }) => (
  <Table head={['תאריך', 'פריטים', 'סכום', 'שולם', 'חוב', 'סטטוס']} rows={logs.map((l) => (
    <tr key={l.id} className="hover:bg-indigo-50 cursor-pointer transition-colors" onClick={() => onOpen(`/daily-log?id=${l.id}`)} data-testid={`card-log-${l.id}`}>
      <td className={td}>{fmtDate(l.date)}</td>
      <td className={td}>{(l.workItems || []).map((i) => i.partName).filter(Boolean).join(', ') || '-'}</td>
      <td className={`${td} font-semibold text-blue-700`}>{money(l.total)}</td>
      <td className={`${td} text-green-700`}>{money(l.paid)}</td>
      <td className={`${td} font-bold ${l.debt > 0.01 ? 'text-red-600' : 'text-gray-400'}`}>{l.debt > 0.01 ? money(l.debt) : '-'}</td>
      <td className={td}>{l.consolidatedInvoiceId ? 'בחשבון מרוכז' : l.accountClosed ? 'סגור' : 'פתוח'}</td>
    </tr>
  ))} />
);

const DealsTable = ({ deals, onOpen }) => (
  <Table head={['תאריך', 'פריטים', 'סכום מוסכם', 'שולם']} rows={deals.map((d) => (
    <tr key={d.id} className="hover:bg-green-50 cursor-pointer transition-colors" onClick={() => onOpen(`/deal-closure?id=${d.id}`)} data-testid={`card-deal-${d.id}`}>
      <td className={td}>{fmtDate(d.date)}</td>
      <td className={td}>{(d.items || []).map((i) => i.partName).filter(Boolean).join(', ') || '-'}</td>
      <td className={`${td} font-semibold text-blue-700`}>{money(d.total)}</td>
      <td className={`${td} text-green-700`}>{money(d.paid)}</td>
    </tr>
  ))} />
);

const InvoicesTable = ({ invoices, onOpen }) => (
  <Table head={['תאריך', 'רישומים', 'סכום', 'שולם', 'יתרה']} rows={invoices.map((inv) => (
    <tr key={inv.id} className="hover:bg-teal-50 cursor-pointer transition-colors" onClick={() => onOpen(`/consolidated-invoice?id=${inv.id}`)} data-testid={`card-invoice-${inv.id}`}>
      <td className={td}>{fmtDate(inv.date)}</td>
      <td className={td}>{(inv.logIds?.length || 0) + (inv.callIds?.length || 0)}</td>
      <td className={`${td} font-semibold text-blue-700`}>{money(inv.totalAmount)}</td>
      <td className={`${td} text-green-700`}>{money(inv.totalPaid)}</td>
      <td className={`${td} font-bold ${inv.balance > 0.01 ? 'text-red-600' : inv.balance < -0.01 ? 'text-blue-600' : 'text-gray-400'}`}>{Math.abs(inv.balance) > 0.01 ? money(inv.balance) : 'מאוזן'}</td>
    </tr>
  ))} />
);

const PaymentsTable = ({ payments }) => (
  <Table head={['תאריך', 'אמצעי', 'סכום', 'מקור', 'פרטים']} rows={payments.map((p, i) => (
    <tr key={`${p.sourceId}-${p.id || i}`} data-testid={`card-payment-${i}`}>
      <td className={td}>{fmtDate(p.date)}</td>
      <td className={td}>{methodLabel(p.method)}</td>
      <td className={`${td} font-bold text-green-700`}>{money(p.amount)}</td>
      <td className={td}>{SOURCE_LABELS[p.source]} ({fmtDate(p.sourceDate)})</td>
      <td className={`${td} text-gray-500`}>{[p.checkNumber && `שיק #${p.checkNumber}`, p.bankName, p.notes].filter(Boolean).join(' | ') || '-'}</td>
    </tr>
  ))} />
);

const PayDialog = ({ open, onOpenChange, card, onOpen }) => {
  const openLogs = card.dailyLogs.filter((l) => l.debt > 0.01);
  const openInvoices = card.consolidatedInvoices.filter((i) => i.balance > 0.01);
  const item = (key, label, amount, path) => (
    <button key={key} type="button" onClick={() => onOpen(path)} className="w-full flex justify-between items-center p-3 rounded-lg border-2 border-gray-200 hover:border-amber-500 hover:bg-amber-50 transition-colors" data-testid={`pay-target-${key}`}>
      <span className="font-medium">{label}</span><span className="font-bold text-red-600">{money(amount)}</span>
    </button>
  );
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" dir="rtl" data-testid="pay-dialog">
        <DialogHeader><DialogTitle className="text-xl font-bold">על מה התשלום?</DialogTitle></DialogHeader>
        <div className="space-y-2 mt-2">
          {openLogs.map((l) => item(l.id, `רישום יומי ${fmtDate(l.date)}`, l.debt, `/daily-log?id=${l.id}`))}
          {openInvoices.map((i) => item(i.id, `חשבון מרוכז ${fmtDate(i.date)}`, i.balance, `/consolidated-invoice?id=${i.id}`))}
          {openLogs.length + openInvoices.length === 0 && <p className="text-center text-gray-500 py-6" data-testid="pay-dialog-empty">אין חובות פתוחים ללקוח זה</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function CustomerCard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [card, setCard] = useState(null);
  const [error, setError] = useState('');
  const [payOpen, setPayOpen] = useState(false);

  useEffect(() => {
    getCustomerCard(id).then(setCard).catch((e) => setError(e.response?.data?.detail || 'לא ניתן לטעון את כרטיס הלקוח'));
  }, [id]);

  if (error) return <div className="min-h-screen flex items-center justify-center text-xl text-red-600" dir="rtl" data-testid="customer-card-error">{error}</div>;
  if (!card) return <div className="min-h-screen flex items-center justify-center text-xl text-gray-500" dir="rtl">טוען כרטיס לקוח...</div>;

  const { customer, summary } = card;
  const isBusiness = customer.customerType === 'business';
  const debt = summary.totalDebt;
  const toggleExempt = async () => {
    try {
      const updated = await updateCustomer(customer.id, { ...customer, vatExempt: !customer.vatExempt });
      setCard({ ...card, customer: { ...customer, vatExempt: !!updated.vatExempt } });
      toast({ title: updated.vatExempt ? 'הלקוח סומן כפטור ממע"מ' : 'הלקוח חויב במע"מ' });
    } catch {
      toast({ title: 'שגיאה', description: 'לא ניתן לעדכן', variant: 'destructive' });
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4 print:bg-white print:py-0" dir="rtl" data-testid="customer-card-page">
      <div className="container mx-auto max-w-6xl">
        <div className="hidden print:block text-center border-b-2 border-gray-300 pb-3 mb-4">
          <h1 className="text-2xl font-bold">אימן שירותי מחשוב</h1>
          <p className="text-xs text-gray-500">aiman@aimanpc.com | 052-576-7555 | הופק: {new Date().toLocaleDateString('he-IL')}</p>
        </div>

        <div className="flex flex-wrap justify-between items-start gap-4 mb-8">
          <div className="flex items-center gap-5">
            <div className={`w-20 h-20 rounded-2xl flex flex-col items-center justify-center text-white shadow-lg ${isBusiness ? 'bg-blue-600' : 'bg-purple-600'}`} data-testid="card-customer-number">
              <span className="text-[10px] opacity-80">מס' לקוח</span>
              <span className="text-3xl font-black font-mono leading-none">{customer.customerNumber}</span>
            </div>
            <div>
              <h1 className="text-4xl font-black text-slate-900" data-testid="card-customer-name">{card.fullName}</h1>
              <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-slate-600">
                <span className="flex items-center gap-1">{isBusiness ? <Building2 className="w-4 h-4" /> : <User className="w-4 h-4" />}{isBusiness ? 'לקוח עסקי' : 'לקוח פרטי'}</span>
                <button type="button" onClick={toggleExempt} className={`px-2 py-0.5 rounded-full text-xs font-bold border print:hidden ${customer.vatExempt ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-500 border-slate-300 hover:border-slate-500'}`} data-testid="card-vat-exempt-toggle">{customer.vatExempt ? 'פטור ממע"מ ✓' : 'סמן כפטור ממע"מ'}</button>
                {customer.phone && <span className="flex items-center gap-1"><Phone className="w-4 h-4" />{customer.phone}</span>}
                {customer.email && <span className="flex items-center gap-1" dir="ltr"><Mail className="w-4 h-4" />{customer.email}</span>}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 print:hidden">
            <Button onClick={() => navigate(`/daily-log?customer=${encodeURIComponent(card.fullName)}`)} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="card-new-log-btn"><Plus className="w-4 h-4 ml-2" />רישום יומי חדש ללקוח</Button>
            <Button onClick={() => setPayOpen(true)} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="card-add-payment-btn"><Wallet className="w-4 h-4 ml-2" />הוסף תשלום</Button>
            <Button onClick={() => window.print()} variant="outline" className="border-2" data-testid="card-print-btn"><Printer className="w-4 h-4 ml-2" />הדפס כרטיס</Button>
            <Button onClick={() => navigate('/customers')} variant="outline" className="border-2" data-testid="card-back-btn"><ArrowRight className="w-4 h-4 ml-2" />חזור</Button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatBox label={debt < -0.01 ? 'עודף ללקוח' : 'יתרת חוב'} value={money(Math.abs(debt))} tone={debt > 0.01 ? 'bg-red-50 border-red-300 text-red-700' : debt < -0.01 ? 'bg-blue-50 border-blue-300 text-blue-700' : 'bg-green-50 border-green-300 text-green-700'} testId="card-total-debt" />
          <StatBox label="סה״כ חיובים (רישום יומי)" value={money(summary.totalBilled)} tone="bg-white border-gray-200 text-slate-800" testId="card-total-billed" />
          <StatBox label="סה״כ שולם" value={money(summary.totalPaid)} tone="bg-white border-gray-200 text-green-700" testId="card-total-paid" />
          <div className="rounded-xl border-2 p-4 bg-white border-gray-200 text-sm space-y-1" data-testid="card-debt-breakdown">
            <p className="text-xs font-semibold text-gray-500">פירוט החוב</p>
            <p className="flex justify-between"><span>רישומים פתוחים</span><span className="font-bold">{money(summary.openLogsDebt)}</span></p>
            <p className="flex justify-between"><span>חשבונות מרוכזים</span><span className="font-bold">{money(summary.consolidatedBalance)}</span></p>
          </div>
        </div>

        <Section icon={ClipboardList} title="רישומים יומיים" count={card.dailyLogs.length} testId="card-section-logs"><LogsTable logs={card.dailyLogs} onOpen={navigate} /></Section>
        <Section icon={Handshake} title="סגירות עסקה" count={card.dealClosures.length} testId="card-section-deals"><DealsTable deals={card.dealClosures} onOpen={navigate} /></Section>
        <Section icon={FileText} title="חשבונות מרוכזים" count={card.consolidatedInvoices.length} testId="card-section-invoices"><InvoicesTable invoices={card.consolidatedInvoices} onOpen={navigate} /></Section>
        <Section icon={Receipt} title="תשלומים" count={card.payments.length} testId="card-section-payments"><PaymentsTable payments={card.payments} /></Section>
      </div>
      <PayDialog open={payOpen} onOpenChange={setPayOpen} card={card} onOpen={navigate} />
      <style>{`@media print { body { print-color-adjust: exact; -webkit-print-color-adjust: exact; } @page { margin: 1cm; } }`}</style>
    </div>
  );
}
